/**
 * The dashboard's dust field, as a shader.
 *
 * This is a port of the tuning artifact Guri built and dialled in ("Time of Day", 2026-08-30), and
 * it is deliberately a PORT rather than an interpretation. The artifact is the specification: the
 * numbers below came off its sliders, and the fragment shader is its shader, transcribed. An
 * earlier pass reached the same brief through two blurred cloud PNGs and CSS opacity, which is a
 * different picture entirely — clouds drift, dust does not, and none of `grain`, `coverage` or
 * `twinkle` means anything to a blurred bitmap.
 *
 * What the field actually is: two coloured sources sitting just off the top corners, falling off
 * exponentially down the page, quantised into `grain`-sized cells, with each cell lit or not by a
 * hash and breathing on its own phase. That last part is the whole trick — the specks do not
 * travel, so nothing crawls, and the field still reads as alive.
 */
import type { TimeOfDay } from './theme';

/** The eight dials the artifact exposes. `hour` is separate — it is a palette, not a dial. */
export interface DashboardFieldEffect {
  /** How bright the brightest dust gets. */
  intensity: number;
  /** How far each corner source spreads sideways. */
  area: number;
  /** How fast it fades down the page; LOWER reaches further. */
  reach: number;
  /** Speck size in px. 3 is the size the app's loader dots use. */
  grain: number;
  /** How many cells light up at all. */
  coverage: number;
  /** Speed of the breathing and the sideways sway. */
  motion: number;
  /** How much each speck breathes on its own phase. */
  twinkle: number;
  /** The height the field is painted into, in px. */
  band: number;
}

/**
 * Guri's tuned values, copied from the artifact's own output panel.
 *
 * `hour` is NOT among them, and that is a decision worth stating: the artifact reports the hour you
 * happened to be previewing, and his read `evening`. Pinning that here would paint an evening sky at
 * nine in the morning, one line under a greeting that already says "Good morning" — `Greeting` reads
 * `timeOfDayFor()` and this follows the same clock. Pass `hour` explicitly to override it.
 */
export const DASHBOARD_FIELD: DashboardFieldEffect = {
  intensity: 0.45,
  area: 0.49,
  reach: 1.9,
  grain: 3,
  coverage: 0.76,
  motion: 2.15,
  twinkle: 0.92,
  band: 460,
};

/**
 * The two source colours per hour, as the artifact draws them — left corner, right corner.
 *
 * `late` carries the artifact's `night` pair. The artifact and this codebase named the fourth hour
 * differently ("night" there, `late` here, which is what `timeOfDayFor` returns and what `GREETING`
 * keys on); the codebase's name wins, because renaming a union to match a prototype would be the
 * prototype deciding the product's vocabulary.
 */
export const FIELD_PALETTE: Record<TimeOfDay, { left: [number, number, number]; right: [number, number, number] }> = {
  morning: { left: [0x4c, 0x8f, 0xe0], right: [0x7f, 0xd1, 0xe8] },
  afternoon: { left: [0xe0, 0xa8, 0x5c], right: [0x9e, 0x9c, 0x6e] },
  evening: { left: [0x8a, 0x66, 0xff], right: [0xe8, 0x6e, 0xa0] },
  late: { left: [0x4e, 0x86, 0xb0], right: [0x74, 0x5c, 0x8c] },
};

export const FIELD_VERTEX_SHADER = `#version 300 es
in vec2 p; void main(){ gl_Position = vec4(p,0.,1.); }`;

/**
 * The artifact's fragment shader, transcribed. Not adapted — transcribed.
 *
 * It ends by mixing the dust into an OPAQUE ground (`uGround`) rather than emitting alpha, and that
 * is load-bearing rather than incidental: the artifact runs its canvas with `alpha:false`, so every
 * pixel it draws is final. Re-deriving this as a transparent layer composited over the page was
 * tried and produced a visibly weaker, thinner field — the same maths on paper, a different picture
 * on screen. The ground is passed in rather than hardcoded so light mode can hand it its own.
 */
export const FIELD_FRAGMENT_SHADER = `#version 300 es
precision highp float;
out vec4 o;
uniform vec2 uRes; uniform float uT, uIntensity, uArea, uReach, uGrain, uCoverage,
  uTwinkle, uMotion, uBand;
uniform vec3 uL, uR, uGround;

float hash(vec2 p){ p = fract(p*vec2(123.34,456.21)); p += dot(p,p+45.32); return fract(p.x*p.y); }

float blob(vec2 uv, vec2 c, float r){
  vec2 d = uv - c; d.y *= 1.35;
  return exp(-dot(d,d)/(r*r)*2.1);
}

void main(){
  vec2 px = gl_FragCoord.xy;
  vec2 uv = vec2(px.x/uRes.x, (uRes.y - px.y)/uBand);   // 0 at the top, 1 at the band's foot
  float sway = sin(uT*0.05*uMotion)*0.025;
  float f = 0.95*blob(uv, vec2(0.07+sway, -0.08), uArea)
          + 0.88*blob(uv, vec2(0.93-sway, -0.05), uArea*0.95);
  f *= exp(-uv.y*uReach);
  f *= step(uv.y, 1.0);

  vec2 cell = floor(vec2(px.x, uRes.y-px.y)/uGrain);
  float h  = hash(cell);
  float ph = hash(cell+17.3)*6.2831;
  // each speck breathes on its own phase, so the field lives without anything crawling
  float br = mix(1.0, 0.5+0.5*sin(uT*0.8*uMotion + ph), uTwinkle);
  float on = step(h, f*uCoverage*br);
  float a  = uIntensity*(0.35+0.65*f)*(0.55+0.45*hash(cell+3.7))*on*br;
  vec3 col = mix(uL, uR, clamp(uv.x,0.,1.));
  o = vec4(mix(uGround, col, clamp(a,0.,1.)), 1.0);
}`;
